import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Save, Building, Upload, Palette, Cloud, FileText } from 'lucide-react';
import RichTextEditor from '@/components/RichTextEditor';
import DocumentSeriesConfig from '@/components/DocumentSeriesConfig';

export default function CompanyConfig({ user }) {
  const [loading, setLoading] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [config, setConfig] = useState({
    name: '',
    trade_name: '',
    tax_id: '',
    address: '',
    city: '',
    postal_code: '',
    country: 'España',
    phone: '',
    phone_extension: '',
    email: '',
    website: '',
    bank_account: '',
    quote_text_after_licenses: '',
    quote_text_after_services: '',
    quote_text_after_equipment: '',
    quote_footer_text: '',
    quote_cover_image: null,
    company_logo: null,
    corporate_color: '#0066B3',
    ovh_application_key: '',
    ovh_application_secret: '',
    ovh_consumer_key: '',
    ovh_endpoint: 'ovh-eu',
    ovh_domain: 'camarero10.com',
    ovh_cname_target: '',
    ovh_ttl: 3600,
    iva_general: 21.0,
    iva_reducido: 10.0,
    iva_superreducido: 4.0,
    igic_general: 7.0,
    igic_reducido: 3.0,
    igic_cero: 0.0,
    recargo_equivalencia_enabled: false,
    recargo_equivalencia_general: 5.2,
    recargo_equivalencia_reducido: 1.4,
    recargo_equivalencia_superreducido: 0.5,
    is_microempresa: false,
    is_pyme: false,
    is_tipo_microempresa_tramo1: 21.0,
    is_tipo_microempresa_tramo2: 22.0,
    is_tipo_pyme: 23.0,
    is_tipo_general: 25.0,
    is_pago_fraccionado_general: 18.0,
    is_pago_fraccionado_grandes: 24.0,
    mercantile_register: '',
    invoice_conditions: '',
    // Variables de envío
    telefono_comercial: '',
    telefono_comercial_ext: '',
    telefono_ventas: '',
    telefono_ventas_ext: '',
    whatsapp_comercial: '',
    whatsapp_ventas: '',
    email_comercial: '',
    email_administracion: '',
    email_general: '',
    email_ventas: '',
    pagina_web: '',
    linkedin: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    twitter_x: '',
    google_my_business: ''
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axiosInstance.get('/company-config');
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading config');
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/company-config/upload-logo', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setConfig({ ...config, company_logo: response.data.logo });
      toast.success('Logo subido y optimizado correctamente');
    } catch (error) {
      toast.error('Error al subir el logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConfig({ ...config, quote_cover_image: e.target.result });
        toast.success('Imagen de portada subida');
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Error al subir la imagen');
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.put('/company-config', config);
      toast.success('Configuración guardada correctamente');
      fetchConfig();
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="company-config-container">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="company-config-title">Configuración de Empresa</h1>
          <p className="text-slate-600">Datos que aparecerán en facturas y documentos</p>
        </div>

        <Tabs defaultValue="general" className="w-full max-w-4xl">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="general">
              <Building className="w-4 h-4 mr-2" />
              General
            </TabsTrigger>
            <TabsTrigger value="series">
              <FileText className="w-4 h-4 mr-2" />
              Series
            </TabsTrigger>
            <TabsTrigger value="documents">
              <Palette className="w-4 h-4 mr-2" />
              Documentos
            </TabsTrigger>
            <TabsTrigger value="variables">
              📧 Variables
            </TabsTrigger>
            <TabsTrigger value="fiscalidad">
              💶 Fiscalidad
            </TabsTrigger>
            <TabsTrigger value="ovh">
              <Cloud className="w-4 h-4 mr-2" />
              OVH
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general">
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Información de la Empresa
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Empresa</Label>
                  <Input
                    id="name"
                    data-testid="config-name-input"
                    value={config.name}
                    onChange={(e) => setConfig({ ...config, name: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trade_name">Nombre Comercial</Label>
                  <Input
                    id="trade_name"
                    data-testid="config-trade-name-input"
                    value={config.trade_name || ''}
                    onChange={(e) => setConfig({ ...config, trade_name: e.target.value })}
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tax_id">NIF/CIF</Label>
                  <Input
                    id="tax_id"
                    data-testid="config-tax-id-input"
                    value={config.tax_id}
                    onChange={(e) => setConfig({ ...config, tax_id: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  data-testid="config-address-input"
                  value={config.address}
                  onChange={(e) => setConfig({ ...config, address: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">Ciudad</Label>
                  <Input
                    id="city"
                    data-testid="config-city-input"
                    value={config.city}
                    onChange={(e) => setConfig({ ...config, city: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Código Postal</Label>
                  <Input
                    id="postal_code"
                    data-testid="config-postal-input"
                    value={config.postal_code}
                    onChange={(e) => setConfig({ ...config, postal_code: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Input
                    id="country"
                    data-testid="config-country-input"
                    value={config.country}
                    onChange={(e) => setConfig({ ...config, country: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <div className="flex gap-2">
                    <Input
                      id="phone"
                      data-testid="config-phone-input"
                      value={config.phone}
                      onChange={(e) => setConfig({ ...config, phone: e.target.value })}
                      required
                      className="flex-1"
                    />
                    <Input
                      id="phone_extension"
                      value={config.phone_extension || ''}
                      onChange={(e) => setConfig({ ...config, phone_extension: e.target.value })}
                      placeholder="Ext."
                      className="w-24"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    data-testid="config-email-input"
                    type="email"
                    value={config.email}
                    onChange={(e) => setConfig({ ...config, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website">Sitio Web (opcional)</Label>
                <Input
                  id="website"
                  data-testid="config-website-input"
                  value={config.website || ''}
                  onChange={(e) => setConfig({ ...config, website: e.target.value })}
                  placeholder="https://www.ejemplo.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bank_account">Cuenta Bancaria / IBAN</Label>
                <Input
                  id="bank_account"
                  value={config.bank_account || ''}
                  onChange={(e) => setConfig({ ...config, bank_account: e.target.value })}
                  placeholder="ES00 0000 0000 0000 0000 0000"
                />
                <p className="text-sm text-gray-500">
                  Se mostrará en los presupuestos para datos de transferencia
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mercantile_register">Registro Mercantil</Label>
                <Input
                  id="mercantile_register"
                  value={config.mercantile_register || ''}
                  onChange={(e) => setConfig({ ...config, mercantile_register: e.target.value })}
                  placeholder="Inscrita en el Registro Mercantil de Madrid, Tomo..., Folio..., Sección..., Hoja..."
                />
                <p className="text-sm text-gray-500">
                  Datos de inscripción en el Registro Mercantil (aparecerá en facturas)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="invoice_conditions">Condiciones de Factura</Label>
                <RichTextEditor
                  value={config.invoice_conditions || ''}
                  onChange={(value) => setConfig({ ...config, invoice_conditions: value })}
                  placeholder="Texto de condiciones que aparecerá al pie de las facturas..."
                  minHeight="100px"
                />
                <p className="text-sm text-gray-500">
                  Condiciones y avisos legales que aparecerán en las facturas emitidas
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote_text_after_licenses">Texto después de Licencias en presupuestos</Label>
                <RichTextEditor
                  value={config.quote_text_after_licenses || ''}
                  onChange={(value) => setConfig({ ...config, quote_text_after_licenses: value })}
                  placeholder="Texto que aparecerá después de la sección de licencias..."
                  minHeight="100px"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote_text_after_services">Texto después de Servicios en presupuestos</Label>
                <RichTextEditor
                  value={config.quote_text_after_services || ''}
                  onChange={(value) => setConfig({ ...config, quote_text_after_services: value })}
                  placeholder="Texto que aparecerá después de la sección de servicios..."
                  minHeight="100px"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote_text_after_equipment">Texto después de Equipos en presupuestos</Label>
                <RichTextEditor
                  value={config.quote_text_after_equipment || ''}
                  onChange={(value) => setConfig({ ...config, quote_text_after_equipment: value })}
                  placeholder="Texto que aparecerá después de la sección de equipos..."
                  minHeight="100px"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="quote_footer_text">Texto Final de Presupuestos/Facturas</Label>
                <RichTextEditor
                  value={config.quote_footer_text || ''}
                  onChange={(value) => setConfig({ ...config, quote_footer_text: value })}
                  placeholder="Texto que aparecerá al final de presupuestos y facturas..."
                  minHeight="120px"
                />
              </div>

              <div className="space-y-3">
                <Label>Imagen de Portada del Presupuesto</Label>
                <p className="text-sm text-gray-500">
                  Esta imagen aparecerá en la primera página del presupuesto
                </p>
                
                <div className="flex items-center gap-4">
                  {config.quote_cover_image && (
                    <div className="border rounded-lg p-4 bg-gray-50">
                      <img 
                        src={config.quote_cover_image} 
                        alt="Imagen portada" 
                        className="max-h-32 max-w-64 object-contain"
                      />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <input
                      type="file"
                      id="cover-upload"
                      accept="image/*"
                      onChange={handleCoverUpload}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('cover-upload').click()}
                      disabled={uploadingLogo}
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {config.quote_cover_image ? 'Cambiar Imagen' : 'Subir Imagen'}
                    </Button>
                    {config.quote_cover_image && (
                      <Button
                        type="button"
                        variant="ghost"
                        className="ml-2 text-red-600"
                        onClick={() => setConfig({ ...config, quote_cover_image: null })}
                      >
                        Eliminar
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Personalización */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Personalización
                </h3>

                {/* Logo de Empresa */}
                <div className="space-y-3 mb-6">
                  <Label>Logo de la Empresa</Label>
                  <p className="text-sm text-gray-500">
                    Este logo aparecerá en el sidebar y en todos los documentos (facturas, presupuestos, etc.)
                  </p>
                  
                  <div className="flex items-center gap-4">
                    {config.company_logo && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <img 
                          src={config.company_logo} 
                          alt="Logo empresa" 
                          className="max-h-24 max-w-48 object-contain"
                        />
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('logo-upload').click()}
                        disabled={uploadingLogo}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        {uploadingLogo ? 'Subiendo...' : config.company_logo ? 'Cambiar Logo' : 'Subir Logo'}
                      </Button>
                      <p className="text-xs text-gray-500 mt-2">
                        Se optimizará automáticamente para web
                      </p>
                    </div>
                  </div>
                </div>

                {/* Color Corporativo */}
                <div className="space-y-3">
                  <Label htmlFor="corporate_color">Color Corporativo</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Este color se aplicará a botones, enlaces y elementos destacados de tu plataforma
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <Input
                        type="color"
                        id="corporate_color"
                        value={config.corporate_color || '#0066B3'}
                        onChange={(e) => setConfig({ ...config, corporate_color: e.target.value })}
                        className="w-24 h-12 cursor-pointer"
                      />
                    </div>
                    <div>
                      <Input
                        type="text"
                        value={config.corporate_color || '#0066B3'}
                        onChange={(e) => setConfig({ ...config, corporate_color: e.target.value })}
                        placeholder="#0066B3"
                        className="font-mono"
                        maxLength={7}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Por defecto: OSAGEST Blue (#0066B3)
                      </p>
                    </div>
                    <div 
                      className="w-24 h-12 rounded border flex items-center justify-center text-white font-semibold text-sm"
                      style={{ backgroundColor: config.corporate_color || '#0066B3' }}
                    >
                      Preview
                    </div>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                style={{ 
                  backgroundColor: config.corporate_color || '#0066B3',
                  color: 'white'
                }}
                disabled={loading}
                data-testid="config-submit-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="series">
        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <DocumentSeriesConfig />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="documents">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Palette className="w-5 h-5" />
              Configuración de Documentos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Contenido existente de documentos - se mantiene igual */}
              <p className="text-sm text-gray-600">
                Esta pestaña contiene la configuración de textos y personalización de documentos.
                <br />
                Los campos se han movido aquí para mejor organización.
              </p>

              <Button
                type="submit"
                className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Guardando...' : 'Guardar Configuración'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="fiscalidad">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              💶 Configuración Fiscal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* IVA Configuration */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-4">📊 IVA (Régimen General)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="iva_general">IVA General (%)</Label>
                    <Input
                      id="iva_general"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.iva_general || 21}
                      onChange={(e) => setConfig({ ...config, iva_general: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="iva_reducido">IVA Reducido (%)</Label>
                    <Input
                      id="iva_reducido"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.iva_reducido || 10}
                      onChange={(e) => setConfig({ ...config, iva_reducido: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="iva_superreducido">IVA Superreducido (%)</Label>
                    <Input
                      id="iva_superreducido"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.iva_superreducido || 4}
                      onChange={(e) => setConfig({ ...config, iva_superreducido: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* IGIC Configuration */}
              <div className="border-b pb-4">
                <h3 className="font-semibold text-lg mb-4">🏝️ IGIC (Canarias)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="igic_general">IGIC General (%)</Label>
                    <Input
                      id="igic_general"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.igic_general || 7}
                      onChange={(e) => setConfig({ ...config, igic_general: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="igic_reducido">IGIC Reducido (%)</Label>
                    <Input
                      id="igic_reducido"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.igic_reducido || 3}
                      onChange={(e) => setConfig({ ...config, igic_reducido: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="igic_cero">Sin IGIC (%)</Label>
                    <Input
                      id="igic_cero"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      value={config.igic_cero || 0}
                      onChange={(e) => setConfig({ ...config, igic_cero: parseFloat(e.target.value) })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Recargo de Equivalencia */}
              <div className="border-b pb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg">➕ Recargo de Equivalencia</h3>
                    <p className="text-sm text-gray-600">Adicional al IVA (no lo reemplaza)</p>
                  </div>
                  <Switch
                    checked={config.recargo_equivalencia_enabled || false}
                    onCheckedChange={(checked) => setConfig({ ...config, recargo_equivalencia_enabled: checked })}
                  />
                </div>
                {config.recargo_equivalencia_enabled && (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="recargo_general">Recargo General (%)</Label>
                      <Input
                        id="recargo_general"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.recargo_equivalencia_general || 5.2}
                        onChange={(e) => setConfig({ ...config, recargo_equivalencia_general: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Por defecto: 5.2%</p>
                    </div>
                    <div>
                      <Label htmlFor="recargo_reducido">Recargo Reducido (%)</Label>
                      <Input
                        id="recargo_reducido"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.recargo_equivalencia_reducido || 1.4}
                        onChange={(e) => setConfig({ ...config, recargo_equivalencia_reducido: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Por defecto: 1.4%</p>
                    </div>
                    <div>
                      <Label htmlFor="recargo_superreducido">Recargo Superreducido (%)</Label>
                      <Input
                        id="recargo_superreducido"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.recargo_equivalencia_superreducido || 0.5}
                        onChange={(e) => setConfig({ ...config, recargo_equivalencia_superreducido: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Por defecto: 0.5%</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Impuesto de Sociedades */}
              <div>
                <h3 className="font-semibold text-lg mb-4">🏢 Impuesto de Sociedades (2025)</h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <Label htmlFor="is_microempresa" className="font-semibold">Microempresa (menos de 1M€ facturación)</Label>
                      <p className="text-xs text-gray-600">Tipos reducidos: 21% hasta 50k, 22% resto (cambio gradual 2025-2027)</p>
                    </div>
                    <Switch
                      id="is_microempresa"
                      checked={config.is_microempresa || false}
                      onCheckedChange={(checked) => setConfig({ ...config, is_microempresa: checked, is_pyme: false })}
                    />
                  </div>

                  {config.is_microempresa && (
                    <div className="grid grid-cols-2 gap-4 ml-4">
                      <div>
                        <Label htmlFor="is_tipo_microempresa_tramo1">Tipo Tramo 1 - Hasta 50k (%)</Label>
                        <Input
                          id="is_tipo_microempresa_tramo1"
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={config.is_tipo_microempresa_tramo1 || 21}
                          onChange={(e) => setConfig({ ...config, is_tipo_microempresa_tramo1: parseFloat(e.target.value) })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">2025: 21%, 2026: 19%, 2027: 17%</p>
                      </div>
                      <div>
                        <Label htmlFor="is_tipo_microempresa_tramo2">Tipo Tramo 2 - Resto (%)</Label>
                        <Input
                          id="is_tipo_microempresa_tramo2"
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={config.is_tipo_microempresa_tramo2 || 22}
                          onChange={(e) => setConfig({ ...config, is_tipo_microempresa_tramo2: parseFloat(e.target.value) })}
                          className="mt-1"
                        />
                        <p className="text-xs text-gray-500 mt-1">2025: 22%, 2026: 21%, 2027: 20%</p>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div>
                      <Label htmlFor="is_pyme" className="font-semibold">PYME (1-10M€ facturación)</Label>
                      <p className="text-xs text-gray-600">Tipo general: 23%</p>
                    </div>
                    <Switch
                      id="is_pyme"
                      checked={config.is_pyme || false}
                      onCheckedChange={(checked) => setConfig({ ...config, is_pyme: checked, is_microempresa: false })}
                    />
                  </div>

                  {config.is_pyme && (
                    <div className="ml-4">
                      <Label htmlFor="is_tipo_pyme">Tipo Impositivo PYME (%)</Label>
                      <Input
                        id="is_tipo_pyme"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.is_tipo_pyme || 23}
                        onChange={(e) => setConfig({ ...config, is_tipo_pyme: parseFloat(e.target.value) })}
                        className="mt-1 w-64"
                      />
                      <p className="text-xs text-gray-500 mt-1">Por defecto: 23%</p>
                    </div>
                  )}

                  {!config.is_microempresa && !config.is_pyme && (
                    <div>
                      <Label htmlFor="is_tipo_general">Tipo General (>10M€ o sin calificación) (%)</Label>
                      <Input
                        id="is_tipo_general"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.is_tipo_general || 25}
                        onChange={(e) => setConfig({ ...config, is_tipo_general: parseFloat(e.target.value) })}
                        className="mt-1 w-64"
                      />
                      <p className="text-xs text-gray-500 mt-1">Por defecto: 25%</p>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div>
                      <Label htmlFor="is_pago_fraccionado_general">Pago Fraccionado General (%)</Label>
                      <Input
                        id="is_pago_fraccionado_general"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.is_pago_fraccionado_general || 18}
                        onChange={(e) => setConfig({ ...config, is_pago_fraccionado_general: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Para empresas &lt;10M€ (Mod. 202)</p>
                    </div>
                    <div>
                      <Label htmlFor="is_pago_fraccionado_grandes">Pago Fraccionado Grandes Empresas (%)</Label>
                      <Input
                        id="is_pago_fraccionado_grandes"
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={config.is_pago_fraccionado_grandes || 24}
                        onChange={(e) => setConfig({ ...config, is_pago_fraccionado_grandes: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Para empresas &gt;10M€ (Mod. 202)</p>
                    </div>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                    <p className="text-sm text-amber-900">
                      <strong>ℹ️ Información:</strong> Los tipos para microempresas cambiarán gradualmente:
                      <br/>• 2025: 21% y 22%
                      <br/>• 2026: 19% y 21%
                      <br/>• 2027: 17% y 20%
                      <br/>Actualiza estos valores cada año fiscal.
                    </p>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Guardando...' : 'Guardar Configuración Fiscal'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>



      {/* ============= TAB: VARIABLES DE ENVÍO ============= */}
      <TabsContent value="variables">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>📧 Variables de Envío</CardTitle>
            <p className="text-sm text-gray-600 mt-2">
              Configura datos de contacto y redes sociales de tu empresa para usar en plantillas de email y WhatsApp
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Teléfonos */}
            <div className="border-b pb-6">
              <h3 className="font-semibold text-lg mb-4">📞 Teléfonos</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telefono_comercial">Teléfono Comercial</Label>
                  <div className="flex gap-2">
                    <Input
                      id="telefono_comercial"
                      value={config.telefono_comercial || ''}
                      onChange={(e) => setConfig({ ...config, telefono_comercial: e.target.value })}
                      placeholder="+34612345678"
                      className="flex-1"
                    />
                    <Input
                      id="telefono_comercial_ext"
                      value={config.telefono_comercial_ext || ''}
                      onChange={(e) => setConfig({ ...config, telefono_comercial_ext: e.target.value })}
                      placeholder="Ext."
                      className="w-20"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Variables: <code className="bg-gray-100 px-1 rounded">{'{telefono_comercial}'}</code> 
                    <code className="bg-gray-100 px-1 rounded ml-1">{'{telefono_comercial_ext}'}</code>
                  </p>
                </div>
                <div>
                  <Label htmlFor="telefono_ventas">Teléfono Ventas</Label>
                  <div className="flex gap-2">
                    <Input
                      id="telefono_ventas"
                      value={config.telefono_ventas || ''}
                      onChange={(e) => setConfig({ ...config, telefono_ventas: e.target.value })}
                      placeholder="+34612345678"
                      className="flex-1"
                    />
                    <Input
                      id="telefono_ventas_ext"
                      value={config.telefono_ventas_ext || ''}
                      onChange={(e) => setConfig({ ...config, telefono_ventas_ext: e.target.value })}
                      placeholder="Ext."
                      className="w-20"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Variables: <code className="bg-gray-100 px-1 rounded">{'{telefono_ventas}'}</code>
                    <code className="bg-gray-100 px-1 rounded ml-1">{'{telefono_ventas_ext}'}</code>
                  </p>
                </div>
                <div>
                  <Label htmlFor="whatsapp_comercial">WhatsApp Comercial</Label>
                  <Input
                    id="whatsapp_comercial"
                    value={config.whatsapp_comercial || ''}
                    onChange={(e) => setConfig({ ...config, whatsapp_comercial: e.target.value })}
                    placeholder="+34612345678"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{whatsapp_comercial}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="whatsapp_ventas">WhatsApp Ventas</Label>
                  <Input
                    id="whatsapp_ventas"
                    value={config.whatsapp_ventas || ''}
                    onChange={(e) => setConfig({ ...config, whatsapp_ventas: e.target.value })}
                    placeholder="+34612345678"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{whatsapp_ventas}'}</code></p>
                </div>
              </div>
            </div>

            {/* Emails */}
            <div className="border-b pb-6">
              <h3 className="font-semibold text-lg mb-4">📧 Emails</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email_comercial">Email Comercial</Label>
                  <Input
                    id="email_comercial"
                    type="email"
                    value={config.email_comercial || ''}
                    onChange={(e) => setConfig({ ...config, email_comercial: e.target.value })}
                    placeholder="comercial@empresa.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{email_comercial}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="email_administracion">Email Administración</Label>
                  <Input
                    id="email_administracion"
                    type="email"
                    value={config.email_administracion || ''}
                    onChange={(e) => setConfig({ ...config, email_administracion: e.target.value })}
                    placeholder="admin@empresa.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{email_administracion}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="email_general">Email General</Label>
                  <Input
                    id="email_general"
                    type="email"
                    value={config.email_general || ''}
                    onChange={(e) => setConfig({ ...config, email_general: e.target.value })}
                    placeholder="info@empresa.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{email_general}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="email_ventas">Email Ventas</Label>
                  <Input
                    id="email_ventas"
                    type="email"
                    value={config.email_ventas || ''}
                    onChange={(e) => setConfig({ ...config, email_ventas: e.target.value })}
                    placeholder="ventas@empresa.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{email_ventas}'}</code></p>
                </div>
              </div>
            </div>

            {/* Web & Redes Sociales */}
            <div className="pb-6">
              <h3 className="font-semibold text-lg mb-4">🌐 Web y Redes Sociales</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pagina_web">Página Web</Label>
                  <Input
                    id="pagina_web"
                    type="url"
                    value={config.pagina_web || ''}
                    onChange={(e) => setConfig({ ...config, pagina_web: e.target.value })}
                    placeholder="https://www.empresa.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{pagina_web}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="linkedin">LinkedIn</Label>
                  <Input
                    id="linkedin"
                    type="url"
                    value={config.linkedin || ''}
                    onChange={(e) => setConfig({ ...config, linkedin: e.target.value })}
                    placeholder="https://linkedin.com/company/empresa"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{linkedin}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="facebook">Facebook</Label>
                  <Input
                    id="facebook"
                    type="url"
                    value={config.facebook || ''}
                    onChange={(e) => setConfig({ ...config, facebook: e.target.value })}
                    placeholder="https://facebook.com/empresa"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{facebook}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="instagram">Instagram</Label>
                  <Input
                    id="instagram"
                    type="url"
                    value={config.instagram || ''}
                    onChange={(e) => setConfig({ ...config, instagram: e.target.value })}
                    placeholder="https://instagram.com/empresa"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{instagram}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="tiktok">TikTok</Label>
                  <Input
                    id="tiktok"
                    type="url"
                    value={config.tiktok || ''}
                    onChange={(e) => setConfig({ ...config, tiktok: e.target.value })}
                    placeholder="https://tiktok.com/@empresa"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{tiktok}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="twitter_x">X (Twitter)</Label>
                  <Input
                    id="twitter_x"
                    type="url"
                    value={config.twitter_x || ''}
                    onChange={(e) => setConfig({ ...config, twitter_x: e.target.value })}
                    placeholder="https://x.com/empresa"
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{twitter_x}'}</code></p>
                </div>
                <div>
                  <Label htmlFor="google_my_business">Google My Business</Label>
                  <Input
                    id="google_my_business"
                    type="url"
                    value={config.google_my_business || ''}
                    onChange={(e) => setConfig({ ...config, google_my_business: e.target.value })}
                    placeholder="https://maps.google.com/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Variable: <code className="bg-gray-100 px-1 rounded">{'{google_my_business}'}</code></p>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-4">
              <Button onClick={handleSubmit} disabled={loading} className="bg-[#9C0F17] hover:bg-[#7A0C12]">
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="ovh">
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="w-5 h-5" />
              Configuración OVH DNS
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg mb-4">
                <p className="text-sm text-blue-900">
                  <strong>Información:</strong> Configura las credenciales de la API de OVH para crear automáticamente registros DNS CNAME cuando se crea una nueva plataforma C10.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="ovh_application_key">Application Key (AK)</Label>
                  <Input
                    id="ovh_application_key"
                    type="text"
                    value={config.ovh_application_key || ''}
                    onChange={(e) => setConfig({ ...config, ovh_application_key: e.target.value })}
                    placeholder="Ingresa tu Application Key de OVH"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="ovh_application_secret">Application Secret (AS)</Label>
                  <Input
                    id="ovh_application_secret"
                    type="password"
                    value={config.ovh_application_secret || ''}
                    onChange={(e) => setConfig({ ...config, ovh_application_secret: e.target.value })}
                    placeholder="Ingresa tu Application Secret de OVH"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="ovh_consumer_key">Consumer Key (CK)</Label>
                  <Input
                    id="ovh_consumer_key"
                    type="password"
                    value={config.ovh_consumer_key || ''}
                    onChange={(e) => setConfig({ ...config, ovh_consumer_key: e.target.value })}
                    placeholder="Ingresa tu Consumer Key de OVH"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="ovh_endpoint">Endpoint</Label>
                  <Select
                    value={config.ovh_endpoint || 'ovh-eu'}
                    onValueChange={(value) => setConfig({ ...config, ovh_endpoint: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona el endpoint" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ovh-eu">ovh-eu (Europa)</SelectItem>
                      <SelectItem value="ovh-ca">ovh-ca (Canadá)</SelectItem>
                      <SelectItem value="ovh-us">ovh-us (USA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="ovh_domain">Dominio Base</Label>
                  <Input
                    id="ovh_domain"
                    type="text"
                    value={config.ovh_domain || ''}
                    onChange={(e) => setConfig({ ...config, ovh_domain: e.target.value })}
                    placeholder="camarero10.com"
                    className="font-mono"
                  />
                </div>

                <div>
                  <Label htmlFor="ovh_cname_target">Dirección Destino CNAME</Label>
                  <Input
                    id="ovh_cname_target"
                    type="text"
                    value={config.ovh_cname_target || ''}
                    onChange={(e) => setConfig({ ...config, ovh_cname_target: e.target.value })}
                    placeholder="servidor.camarero10.com"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Dirección a la que apuntarán los registros CNAME creados
                  </p>
                </div>

                <div>
                  <Label htmlFor="ovh_ttl">TTL (segundos)</Label>
                  <Input
                    id="ovh_ttl"
                    type="number"
                    value={config.ovh_ttl || 3600}
                    onChange={(e) => setConfig({ ...config, ovh_ttl: parseInt(e.target.value) })}
                    placeholder="3600"
                    min="60"
                    max="86400"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Por defecto: 3600 segundos (1 hora)
                  </p>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                disabled={loading}
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Guardando...' : 'Guardar Configuración OVH'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </TabsContent>

    </Tabs>
      </div>
    </Layout>
  );
}
